import apiClient from "../common";
import { prefetchApiCsrfToken } from "../common";
import type { EstimatedCoverage, ProductCoverage, ProductProps } from "../types";

const JSON_API_MEDIA = "application/vnd.api+json";

export type CoveragePremiseRow = {
  name: string;
  value: unknown;
};

export type SimulationBootstrapPayload = {
  simulationId: string;
  /** Presente al crear simulación (POST); omitido al reabrir histórico (GET + cobertura v2). */
  simulationResultPayload?: unknown;
  /** Metadatos del GET detalle al reabrir histórico (Agente 6). */
  simulationName?: string;
  simulatedPurchaseOrders?: { id: string; document_number: string }[];
};

function stringifyParam(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).trim();
}

/** Fecha local `YYYY-MM-DD` para `simulation_name` al crear simulación desde premisas. */
export function todayLocalIsoDate(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Convierte las premisas de cobertura en query params del POST `/simulations/`. */
export function simulationQueryParamsFromPremises(
  premises: CoveragePremiseRow[],
): Record<string, string | number> {
  const qp: Record<string, string | number> = {};

  const findValue = (key: string): string => {
    const row = premises.find((p) => String(p.name).trim() === key);
    return stringifyParam(row?.value);
  };

  const tcd = findValue("target_coverage_days");
  const pm = findValue("projection_months");
  if (!tcd) {
    throw new Error("Parámetro requerido: días objetivo de cobertura.");
  }
  if (!pm) {
    throw new Error("Parámetro requerido: meses de proyección.");
  }
  qp.target_coverage_days = parseInt(tcd, 10);
  qp.projection_months = parseInt(pm, 10);
  if (Number.isNaN(qp.target_coverage_days)) {
    throw new Error("target_coverage_days debe ser un número entero.");
  }
  if (Number.isNaN(qp.projection_months) || qp.projection_months < 1) {
    throw new Error("projection_months debe ser un entero mayor que cero.");
  }

  for (const p of premises) {
    const name = String(p.name).trim();
    const val = stringifyParam(p.value);
    if (!val || val === "all") {
      continue;
    }

    if (name.startsWith("filter_product_")) {
      qp[name.replace(/^filter_product_/, "")] = val;
      continue;
    }

    if (name.startsWith("filter_attribute_product_")) {
      const suffix = name
        .replace(/^filter_attribute_product_/, "")
        .toLowerCase();
      if (suffix === "material_target") {
        qp.material_target = val;
      } else if (suffix === "vendor_code") {
        qp.vendor_code = val;
      }
    }
  }

  /* Desplazamiento opcional conocido como ataw */
  const ataw = findValue("ataw") || findValue("ata_w");
  if (ataw !== "") {
    const n = parseInt(ataw, 10);
    if (!Number.isNaN(n)) {
      qp.ataw = n;
    }
  }

  return qp;
}

function parseJsonApiResultRows(payload: unknown): Record<string, unknown>[] {
  if (payload == null) {
    return [];
  }
  if (!Array.isArray(payload) && typeof payload === "object") {
    const body = payload as { data?: unknown };
    if (Array.isArray(body.data)) {
      return body.data as Record<string, unknown>[];
    }
  }
  if (Array.isArray(payload)) {
    return payload as Record<string, unknown>[];
  }
  return [];
}

function simulationMeta(payload: unknown): Record<string, unknown> | null {
  if (payload && typeof payload === "object") {
    const m = (payload as { meta?: unknown }).meta;
    if (m && typeof m === "object") {
      return m as Record<string, unknown>;
    }
  }
  return null;
}

/** Extrae `simulation_id` de la respuesta 201 JSON:API. */
export function readSimulationIdFromCreateResponse(
  payload: unknown,
): string | undefined {
  const meta = simulationMeta(payload);
  if (!meta) {
    const nested =
      payload && typeof payload === "object"
        ? (payload as { data?: { meta?: unknown } }).data?.meta
        : null;
    if (nested && typeof nested === "object") {
      return readSimulationIdFromMeta(nested as Record<string, unknown>);
    }
    return undefined;
  }
  return readSimulationIdFromMeta(meta);
}

function readSimulationIdFromMeta(
  meta: Record<string, unknown>,
): string | undefined {
  const sid =
    meta.simulation_id ??
    meta.simulationId ??
    meta["simulation-id"];
  if (sid === undefined || sid === null) {
    return undefined;
  }
  return String(sid);
}

function decimalsToNum(v: unknown): number {
  if (v === null || v === undefined) {
    return 0;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Número de semana ISO (alineado a encabezados «Semana N» del simulador). */
function isoWeekForLocalDate(d: Date): number {
  const date = new Date(d.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return (
    1 +
    Math.round(
      ((date.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getDay() + 6) % 7)) /
        7,
    )
  );
}

function targetDatePartsForPeriodStart(d: Date): {
  year: number;
  month: number;
  week: number;
} {
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const week = Math.max(1, isoWeekForLocalDate(d));
  return { year, month, week };
}

function attrsOf(row: Record<string, unknown>): Record<string, unknown> {
  return (row.attributes as Record<string, unknown> | undefined) ?? row;
}

function productPropsLookup(
  productId: string,
  products: ProductProps[],
): ProductProps {
  const idStr = productId.trim();
  const hit = products.find(
    (p) =>
      String(p.erp_code ?? p.erpCode ?? "").trim() === idStr ||
      String(p.id) === idStr,
  );
  if (hit) {
    return { ...hit, erpCode: hit.erp_code ?? hit.erpCode ?? idStr };
  }
  const n = Number(idStr);
  return {
    id: Number.isFinite(n) ? n : 0,
    erpCode: idStr,
    description: idStr,
    category: "",
    line: "",
    subLine: "",
  };
}

/**
 * Transforma líneas JSON:API `simulation-result-products` al modelo de tabla del simulador.
 */
export function mapSimulationResultPayloadToCoverage(
  payload: unknown,
  products: ProductProps[],
): ProductCoverage[] {
  const rows = parseJsonApiResultRows(payload);
  if (rows.length === 0) {
    return [];
  }

  type RowParsed = {
    product_id: string;
    period_start: string;
    est: EstimatedCoverage;
  };

  const parsed: RowParsed[] = [];
  for (const row of rows) {
    const a = attrsOf(row);
    const productId = String(a.product_id ?? "").trim();
    if (!productId) {
      continue;
    }
    const periodRaw = String(a.period_start_date ?? "");
    const d = periodRaw ? new Date(periodRaw + "T12:00:00") : new Date();
    const ym = targetDatePartsForPeriodStart(d);

    const alert = stringifyParam(a.coverage_status);
    const forecastVal = decimalsToNum(a.forecast_qty);
    const outcomesVal = forecastVal;

    const est: EstimatedCoverage = {
      targetDate: {
        year: ym.year,
        month: ym.month,
        week: ym.week,
      },
      current: {
        inventory: decimalsToNum(a.inventory_initial_qty),
        sales: decimalsToNum(a.sales),
        forecast: decimalsToNum(a.forecast_qty),
      },
      estimatedUnits: decimalsToNum(a.inventory_projected_qty),
      coverageTime: decimalsToNum(a.coverage_days),
      events: {
        incomes: { base: 0, simulation: 0 },
        outcomes: outcomesVal,
        alert,
      },
    };
    parsed.push({ product_id: productId, period_start: periodRaw, est });
  }

  parsed.sort((x, y) =>
    `${x.product_id}\t${x.period_start}`.localeCompare(
      `${y.product_id}\t${y.period_start}`,
    ),
  );

  const byPid = new Map<string, EstimatedCoverage[]>();
  for (const r of parsed) {
    const list = byPid.get(r.product_id) ?? [];
    list.push(r.est);
    byPid.set(r.product_id, list);
  }

  return [...byPid.keys()].sort().map((pid) => ({
    productProps: productPropsLookup(pid, products),
    coverage: byPid.get(pid) ?? [],
  }));
}

export async function apiCreateSimulation(opts: {
  premiseRows: CoveragePremiseRow[];
  /** Por defecto fecha local `YYYY-MM-DD`. */
  simulationName?: string;
  /** Por defecto `--`. */
  description?: string;
  /** Por defecto `draft`. `created_by` lo asigna el backend con el usuario de la sesión. */
  status?: string;
}): Promise<SimulationBootstrapPayload> {
  await prefetchApiCsrfToken();
  const params = simulationQueryParamsFromPremises(opts.premiseRows);
  const body = {
    data: {
      type: "simulations",
      attributes: {
        simulation_name: opts.simulationName ?? todayLocalIsoDate(),
        description: opts.description ?? "--",
        status: opts.status ?? "draft",
      },
    },
  };
  const res = await apiClient.post("simulations/", body, {
    params,
    headers: {
      Accept: JSON_API_MEDIA,
      "Content-Type": JSON_API_MEDIA,
    },
  });
  const simId = readSimulationIdFromCreateResponse(res.data);
  if (!simId) {
    throw new Error(
      "La API no devolvió meta.simulation_id en la creación de simulación.",
    );
  }
  return { simulationId: simId, simulationResultPayload: res.data };
}

/** Re-ejecuta cobertura sobre una simulación existente (POST reload, sin crear registro nuevo). */
export async function apiReloadSimulation(
  simulationId: string,
  opts: { premiseRows: CoveragePremiseRow[] },
): Promise<SimulationBootstrapPayload> {
  await prefetchApiCsrfToken();
  const params = simulationQueryParamsFromPremises(opts.premiseRows);
  const res = await apiClient.post(`simulations/${simulationId}/reload/`, null, {
    params,
    headers: {
      Accept: JSON_API_MEDIA,
      "Content-Type": JSON_API_MEDIA,
    },
  });
  return { simulationId, simulationResultPayload: res.data };
}
